class LogoSlider extends HTMLElement {
  connectedCallback() {
    setTimeout(() => this.init(), 0);
  }

  init() {
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = Array.from(this.querySelectorAll('[id^="Slide-"]'));
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');
    this.dotsContainer = this.querySelector(".logo-slider__dots");
    this.visibleSlides = new Set();
    this.intersectionObserver = new IntersectionObserver(
      this.handleIntersection.bind(this),
      {
        root: this.slider,
        threshold: 1.0,
      }
    );

    if (!this.slider || !this.prevButton || !this.nextButton) return;

    // Reset scroll position
    this.slider.scrollLeft = 0;

    this.slider.addEventListener(
      "onscrollend" in window ? "scrollend" : "scroll",
      this.update.bind(this),
      { passive: true },
    );

    this.prevButton.addEventListener("click", () => this.scrollByPage(-1));
    this.nextButton.addEventListener("click", () => this.scrollByPage(1));
    this.resizeObserver = new ResizeObserver(() => this.updatePageCount());
    this.resizeObserver.observe(this.slider);
    this.sliderItems.forEach((item) => this.resizeObserver.observe(item));
    this.sliderItems.forEach((item) => this.intersectionObserver.observe(item));
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    this.intersectionObserver?.disconnect();
  }

  getPageBoundaries() {
    const boundaries = [0];
    const first = this.sliderItems[0];
    let pageStart = 0;

    this.sliderItems.forEach((item) => {
      const itemStart = item.offsetLeft - first.offsetLeft;
      const itemEnd = itemStart + item.offsetWidth;
      if (itemEnd - pageStart > this.slider.clientWidth) {
        boundaries.push(itemStart);
        pageStart = itemStart;
      }
    });

    return boundaries;
  }

  get maxScrollLeft() {
    return Math.max(0, this.slider.scrollWidth - this.slider.clientWidth);
  }

  get slideWidth() {
    // include gap if your CSS uses gap/margin between items
    const item = this.sliderItems[0];
    const style = getComputedStyle(this.slider);
    const gap = parseFloat(style.columnGap || style.gap || 0);
    return item.offsetWidth + gap;
  }

  get slidesPerPage() {
    return Math.max(1, Math.floor(this.slider.clientWidth / this.slideWidth));
  }

  get pageScrollWidth() {
    return this.slidesPerPage * this.slideWidth;
  }

  scrollByPage(direction) {
    this.goToPage(this.currentPage + direction);
  }


  updatePageCount() {
    this.pageBoundaries = this.getPageBoundaries();
    const totalPages = this.pageBoundaries.length;
    if (totalPages === this.totalPages) return;
    this.totalPages = totalPages;
    this.buildDots();
    this.update();
  }

  buildDots() {
    if (!this.dotsContainer) return;
    this.dotsContainer.innerHTML = "";

    for (let i = 0; i < this.totalPages; i++) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "logo-slider__dot";
      btn.setAttribute("aria-label", `Page ${i + 1} of ${this.totalPages}`);
      btn.addEventListener("click", () => this.goToPage(i));
      this.dotsContainer.appendChild(btn);
    }
    this.dots = Array.from(this.dotsContainer.children);
  }

  get currentPage() {
    if (!this.pageBoundaries?.length) return 0;
    let closest = 0, minDiff = Infinity;
    this.pageBoundaries.forEach((b, i) => {
      const diff = Math.abs(this.slider.scrollLeft - b);
      if (diff < minDiff) { minDiff = diff; closest = i; }
    });
    return closest;
  }

  goToPage(page) {
    const left = Math.min(this.pageBoundaries[page] ?? this.maxScrollLeft, this.maxScrollLeft);
    this.slider.scrollTo({
      left,
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "instant"
        : "smooth",
    });
  }

  update() {
    const page = this.currentPage;
    this.dots?.forEach((dot, i) => {
      dot.classList.toggle("logo-slider__dot--active", i === page);
      dot.setAttribute("aria-current", i === page ? "true" : "false");
    });

    this.prevButton.setAttribute(
      "aria-disabled",
      this.slider.scrollLeft <= 0 ? "true" : "false",
    );
    this.nextButton.setAttribute(
      "aria-disabled",
      this.slider.scrollLeft >= this.maxScrollLeft - 1 ? "true" : "false",
    );
  }

  handleIntersection(entries) {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        this.visibleSlides.add(entry.target);
        entry.target.removeAttribute("aria-hidden");
        entry.target.inert = false;
      } else {
        this.visibleSlides.delete(entry.target);
        entry.target.setAttribute("aria-hidden", "true");
        entry.target.inert = true; // also removes it from tab order, disables clicks
      }
    });
    this.scheduleAnnouncement();
  }

  scheduleAnnouncement() {
    clearTimeout(this._announceTimer);
    this._announceTimer = setTimeout(() => this.announceVisibleSlides(), 800);
  }

  announceVisibleSlides() {
    const numItems = this.sliderItems.length;
    const visible = this.sliderItems.filter((item) => this.visibleSlides.has(item));
    if (!visible.length) return;

    const firstIndex = this.sliderItems.indexOf(visible[0]) + 1;
    const lastIndex = this.sliderItems.indexOf(visible[visible.length - 1]) + 1;

    const message =
      firstIndex === lastIndex
        ? `Showing slide ${firstIndex} of ${numItems}`
        : `Showing slides ${firstIndex} to ${lastIndex} of ${numItems}`;

    this.announceToScreenReader(message);
  }

  announceToScreenReader(message) {
    if (!this.liveRegion) {
      this.liveRegion = document.createElement("div");
      this.liveRegion.setAttribute("aria-live", "polite");
      this.liveRegion.className = "visually-hidden";
      this.appendChild(this.liveRegion);
    }
    this.liveRegion.textContent = message;
  }
}

customElements.define("logo-slider", LogoSlider);
